import { Grid } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Typeahead } from "react-bootstrap-typeahead";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import * as XLSX from "xlsx";
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useSelector } from "react-redux";
import { fetchApi, postApi } from "redux/action/api_tbeams";
import { formatNumber } from "redux/action/api";
import { useToasts } from "helper/ToastAdapter";
import { getDistinctObjects } from "layouts/tables/CapitalProject";

const TEMPLATE_COLUMNS = [
  "admin_code",
  "admin_description",
  "economic_code",
  "economic_description",
  "program_code",
  "program_description",
  "amount",
  "input_type",
];

const SAMPLE_ROWS = [
  {
    admin_code: "011100100100",
    admin_description: "Government House",
    economic_code: "22020102",
    economic_description: "LOCAL TRAVEL & TRANSPORT: OTHERS",
    program_code: "011100700100",
    program_description: "General Administration",
    amount: 50000,
    input_type: "virement",
  },
  {
    admin_code: "011100600100",
    admin_description: "Ministry of Budget and Economic Planning",
    economic_code: "21010101",
    economic_description: "Salary",
    program_code: "052100200100",
    program_description: "Budget Preparation",
    amount: 75000,
    input_type: "adjustment",
  },
];

// New Virement form. Mirrors the New Release Collection form (same budget
// summary source). Entries can be typed one-by-one and added to a batch, or
// imported from an Excel file, then submitted together to `post-virement`,
// which writes to the `virement` and `budget_entries` tables.
export default function NewVirementEntry() {
  const navigate = useNavigate();
  const user = useSelector((e) => e.auth.user);
  const { addToast } = useToasts();

  const adminRef = useRef();
  const economicRef = useRef();
  const programRef = useRef();

  const [steps, setSteps] = useState("1");
  const [fetching, setFetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [budgetSummary, setBudgetSummary] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [distinctAdmins, setDistinctAdmins] = useState([]);
  const [selectedData, setSelectedData] = useState([]);
  const [errors, setErrors] = useState([]);

  const emptyForm = useCallback(
    () => ({
      query_type: "insert",
      admin_code: "",
      admin_description: "",
      economic_code: "",
      economic_description: "",
      program_code: "",
      program_description: "",
      amount: "",
      input_type: "virement",
      userId: user?.username || "",
    }),
    [user]
  );

  const [form, setForm] = useState(emptyForm());

  const queryType = steps === "1" ? "recurrent" : "capital";

  const getList = useCallback(() => {
    setFetching(true);
    fetchApi(`get-budget-summary?query_type=${queryType}`)
      .then((d) => {
        const rows = d?.result || [];
        setBudgetSummary(rows);
        setDistinctAdmins(
          getDistinctObjects(rows, "admin_code", "admin_description")
        );
      })
      .catch((err) => console.log(err))
      .finally(() => setFetching(false));
  }, [queryType]);

  useEffect(() => {
    getList();
  }, [getList]);

  const handleChange = ({ target: { name, value } }) =>
    setForm((p) => ({ ...p, [name]: value }));

  const handleAdmin = (selected) => {
    if (selected?.length) {
      const item = selected[0];
      const rows = budgetSummary.filter(
        (r) =>
          r.admin_code === item.admin_code || r.mda_code === item.admin_code
      );
      setFilteredRows(rows);
      setForm((p) => ({
        ...p,
        admin_code: item.admin_code,
        admin_description: item.admin_description,
        economic_code: "",
        economic_description: "",
        program_code: "",
        program_description: "",
      }));
    } else {
      setFilteredRows([]);
      setForm((p) => ({
        ...p,
        admin_code: "",
        admin_description: "",
        economic_code: "",
        economic_description: "",
        program_code: "",
        program_description: "",
      }));
    }
  };

  const handleEconomic = (selected) => {
    if (selected?.length) {
      const item = selected[0];
      setForm((p) => ({
        ...p,
        economic_code: item.economic_code,
        economic_description: item.economic_description,
      }));
    }
  };

  const handleProgram = (selected) => {
    if (selected?.length) {
      const item = selected[0];
      setForm((p) => ({
        ...p,
        program_code: item.program_code,
        program_description: item.program_description,
      }));
    }
  };

  const resetForm = () => {
    adminRef?.current?.clear();
    economicRef?.current?.clear();
    programRef?.current?.clear();
    setFilteredRows([]);
    setForm(emptyForm());
  };

  const handleAddToList = () => {
    // Recurrent entries are dimensioned by economic code, capital entries by
    // program code.
    const isRecurrent = steps === "1";
    const hasDimension = isRecurrent ? form.economic_code : form.program_code;
    const amountValue = Number(form.amount);
    if (
      !form.admin_code ||
      !hasDimension ||
      form.amount === "" ||
      form.amount === null ||
      form.amount === undefined ||
      isNaN(amountValue) ||
      amountValue === 0
    ) {
      addToast(
        isRecurrent
          ? "Please select an MDA and an economic code, and enter an amount."
          : "Please select an MDA and a program code, and enter an amount.",
        {
          appearance: "error",
          autoDismiss: true,
          autoDismissTimeout: 5000,
        }
      );
      return;
    }
    setSelectedData((prev) => [...prev, { ...form, id: Date.now() }]);
    resetForm();
    addToast("Entry added to the list", {
      appearance: "success",
      autoDismiss: true,
      autoDismissTimeout: 3000,
    });
  };

  const handleDeleteFromList = (index) => {
    setSelectedData((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClearList = () => setSelectedData([]);

  const downloadTemplate = () => {
    const worksheet = XLSX.utils.json_to_sheet(SAMPLE_ROWS, {
      header: TEMPLATE_COLUMNS,
    });
    worksheet["!cols"] = [
      { wch: 16 },
      { wch: 32 },
      { wch: 16 },
      { wch: 36 },
      { wch: 16 },
      { wch: 30 },
      { wch: 14 },
      { wch: 14 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Virement");
    XLSX.writeFile(workbook, "Virement_Template.xlsx");
  };

  const validateExcelData = (data) => {
    const validationErrors = [];
    if (!data.length) {
      validationErrors.push("Excel file is empty");
      return validationErrors;
    }

    const columnMap = {};
    Object.keys(data[0]).forEach((col) => {
      columnMap[col.toLowerCase().trim()] = col;
    });

    const missing = TEMPLATE_COLUMNS.filter(
      (col) => !columnMap[col.toLowerCase()]
    );
    if (missing.length) {
      validationErrors.push(
        `Missing required columns: ${missing.join(", ")}. Found columns: ${Object.keys(
          data[0]
        ).join(", ")}`
      );
      return validationErrors;
    }

    data.forEach((row, index) => {
      const rowNumber = index + 2;
      const adminCode = `${row[columnMap["admin_code"]] ?? ""}`.trim();
      const economicCode = `${row[columnMap["economic_code"]] ?? ""}`.trim();
      const programCode = `${row[columnMap["program_code"]] ?? ""}`.trim();
      if (!adminCode) {
        validationErrors.push(`Row ${rowNumber}: admin_code is required`);
      }
      if (!economicCode && !programCode) {
        validationErrors.push(
          `Row ${rowNumber}: an economic_code (recurrent) or program_code (capital) is required`
        );
      }
      const amount = parseFloat(row[columnMap["amount"]]);
      // Negative amounts are allowed (e.g. adjustments/reversals).
      if (isNaN(amount) || amount === 0) {
        validationErrors.push(`Row ${rowNumber}: amount must be a non-zero number`);
      }
      const inputType = `${row[columnMap["input_type"]] || ""}`
        .trim()
        .toLowerCase();
      if (inputType && inputType !== "virement" && inputType !== "adjustment") {
        validationErrors.push(
          `Row ${rowNumber}: input_type must be "virement" or "adjustment"`
        );
      }
    });

    return validationErrors;
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          const validationErrors = validateExcelData(jsonData);
          if (validationErrors.length) {
            setErrors(validationErrors);
            addToast("Excel validation failed. Please check the errors.", {
              appearance: "error",
              autoDismiss: true,
              autoDismissTimeout: 5000,
            });
            return;
          }

          const columnMap = {};
          Object.keys(jsonData[0]).forEach((col) => {
            columnMap[col.toLowerCase().trim()] = col;
          });

          const imported = jsonData.map((row, index) => {
            const get = (col) => `${row[columnMap[col]] ?? ""}`.trim();
            const inputType = get("input_type").toLowerCase();
            return {
              id: `${Date.now()}-${index}`,
              query_type: "insert",
              admin_code: get("admin_code"),
              admin_description: get("admin_description"),
              economic_code: get("economic_code"),
              economic_description: get("economic_description"),
              program_code: get("program_code"),
              program_description: get("program_description"),
              amount: parseFloat(row[columnMap["amount"]]) || 0,
              input_type: inputType === "adjustment" ? "adjustment" : "virement",
              userId: user?.username || "",
            };
          });

          setSelectedData((prev) => [...prev, ...imported]);
          setErrors([]);
          addToast(`Imported ${imported.length} row(s) from Excel`, {
            appearance: "success",
            autoDismiss: true,
            autoDismissTimeout: 3000,
          });
        } catch (error) {
          console.error("Error reading Excel file:", error);
          setErrors([
            "Failed to read Excel file. Please ensure it's a valid .xlsx or .xls file.",
          ]);
          addToast("Failed to read Excel file", {
            appearance: "error",
            autoDismiss: true,
            autoDismissTimeout: 5000,
          });
        }
      };
      reader.readAsArrayBuffer(file);
    },
    [addToast, user]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
    },
    multiple: false,
  });

  const handleSubmit = () => {
    if (!selectedData.length) {
      addToast("Please add at least one entry to the list.", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 5000,
      });
      return;
    }
    setIsLoading(true);
    postApi("post-virement", { query_type: "insert", list: selectedData })
      .then((data) => {
        if (data.success) {
          addToast(
            data.message || "Virement saved successfully",
            {
              appearance: "success",
              autoDismiss: true,
              autoDismissTimeout: 5000,
            }
          );
          setSelectedData([]);
          navigate("/Virement");
        } else {
          addToast(data.message || "Unable to save virement", {
            appearance: "error",
            autoDismiss: true,
            autoDismissTimeout: 5000,
          });
        }
      })
      .catch((err) => {
        addToast(String(err), {
          appearance: "error",
          autoDismiss: true,
          autoDismissTimeout: 5000,
        });
      })
      .finally(() => setIsLoading(false));
  };

  const totalAmount = selectedData.reduce(
    (sum, item) => sum + (parseFloat(item.amount) || 0),
    0
  );

  return (
    <DashboardLayout>
      <MDBox pt={2} pb={3} pl={2}>
        <Grid container spacing={5}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h5" color="white">
                  New Virement
                </MDTypography>
              </MDBox>

              <Col
                md={5}
                className="mt-4"
                style={{
                  marginLeft: 9,
                }}
              >
                <button
                  className="btn btn-success"
                  style={{ marginRight: "10px", marginLeft: "10px" }}
                  onClick={() => navigate("/Virement")}
                >
                  Click here to go back
                </button>
              </Col>

              <Row className="mt-1 px-4 py-4">
                <Col md={4}>
                  <Label>Virement Type</Label>
                  <Input
                    type="select"
                    value={steps}
                    onChange={({ target: { value } }) => {
                      setSteps(value);
                      resetForm();
                    }}
                  >
                    <option value="1">Recurrent</option>
                    <option value="2">Capital</option>
                  </Input>
                </Col>

                <Col md={4}>
                  <Label>Select MDA</Label>
                  <Typeahead
                    size="sm"
                    id="virement-admin"
                    ref={adminRef}
                    onChange={handleAdmin}
                    options={distinctAdmins.length ? distinctAdmins : []}
                    labelKey={(option) =>
                      `${option.admin_description} - ${option.admin_code}`
                    }
                    placeholder="Select MDA"
                  />
                  {fetching && <Spinner size="sm" />}
                </Col>
                <Col md={4}>
                  <Label>Admin Code</Label>
                  <Input
                    type="text"
                    value={form.admin_code}
                    name="admin_code"
                    onChange={handleChange}
                    disabled
                  />
                </Col>

                <Col md={4}>
                  <Label>Admin Description</Label>
                  <Input
                    type="text"
                    value={form.admin_description}
                    name="admin_description"
                    onChange={handleChange}
                    disabled
                  />
                </Col>

                {/* Recurrent entries are dimensioned by economic code. */}
                {steps === "1" && (
                  <>
                    <Col md={4}>
                      <Label>Select Economic Code</Label>
                      <Typeahead
                        size="sm"
                        id="virement-economic"
                        ref={economicRef}
                        onChange={handleEconomic}
                        options={getDistinctObjects(
                          filteredRows.filter(
                            (r) => r.economic_code && r.economic_description
                          ),
                          "economic_code",
                          "economic_description"
                        )}
                        labelKey={(option) =>
                          `${option.economic_code} - ${option.economic_description}`
                        }
                        placeholder="Select Economic Code"
                      />
                    </Col>
                    <Col md={4}>
                      <Label>Economic Description</Label>
                      <Input
                        type="text"
                        value={form.economic_description}
                        name="economic_description"
                        onChange={handleChange}
                        disabled
                      />
                    </Col>
                  </>
                )}

                {/* Capital entries are dimensioned by program code. */}
                {steps === "2" && (
                  <>
                    <Col md={4}>
                      <Label>Select Program Code</Label>
                      <Typeahead
                        size="sm"
                        id="virement-program"
                        ref={programRef}
                        onChange={handleProgram}
                        options={getDistinctObjects(
                          filteredRows.filter(
                            (r) => r.program_code && r.program_description
                          ),
                          "program_code",
                          "program_description"
                        )}
                        labelKey={(option) =>
                          `${option.program_code} - ${option.program_description}`
                        }
                        placeholder="Select Program Code"
                      />
                    </Col>
                    <Col md={4}>
                      <Label>Program Description</Label>
                      <Input
                        type="text"
                        value={form.program_description}
                        name="program_description"
                        onChange={handleChange}
                        disabled
                      />
                    </Col>
                  </>
                )}

                <Col md={4}>
                  <Label>Amount ({formatNumber(form.amount)})</Label>
                  <Input
                    type="number"
                    value={form.amount}
                    name="amount"
                    onChange={handleChange}
                  />
                </Col>

                <Col md={4}>
                  <Label>Input Type</Label>
                  <Input
                    type="select"
                    value={form.input_type}
                    name="input_type"
                    onChange={handleChange}
                  >
                    <option value="virement">Virement</option>
                    <option value="adjustment">Adjustment</option>
                  </Input>
                </Col>

                <Col md={12} className="mt-4">
                  <center>
                    <Button
                      color="primary"
                      onClick={handleAddToList}
                      disabled={isLoading}
                    >
                      <i className="fas fa-plus" style={{ marginRight: "6px" }}></i>
                      Add to List
                    </Button>
                  </center>
                </Col>
              </Row>

              {/* Excel upload */}
              <Row className="px-4">
                <Col md={12}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "15px",
                      borderRadius: "8px",
                      borderLeft: "4px solid #2196f3",
                    }}
                  >
                    <MDTypography
                      variant="h6"
                      style={{ marginBottom: "10px", color: "#2c3e50" }}
                    >
                      Upload from Excel
                    </MDTypography>
                    <Button
                      color="info"
                      size="sm"
                      onClick={downloadTemplate}
                      style={{ marginBottom: "12px" }}
                    >
                      <i
                        className="fas fa-download"
                        style={{ marginRight: "6px" }}
                      ></i>
                      Download Excel Template
                    </Button>
                    <div
                      {...getRootProps()}
                      style={{
                        border: "2px dashed #4caf50",
                        borderRadius: "8px",
                        padding: "24px",
                        textAlign: "center",
                        backgroundColor: isDragActive ? "#e8f5e9" : "#fff",
                        cursor: "pointer",
                      }}
                    >
                      <input {...getInputProps()} />
                      <p style={{ margin: 0, color: "#2c3e50" }}>
                        {isDragActive
                          ? "Drop the Excel file here..."
                          : "Drag and drop an Excel file here, or click to select (.xlsx, .xls)"}
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>

              {errors.length > 0 && (
                <Row className="px-4 mt-3">
                  <Col md={12}>
                    <Alert color="danger" style={{ borderRadius: "8px" }}>
                      <h5 style={{ marginBottom: "10px" }}>
                        <i
                          className="fas fa-exclamation-triangle"
                          style={{ marginRight: "8px" }}
                        ></i>
                        Validation Errors ({errors.length})
                      </h5>
                      <ul style={{ marginBottom: 0, paddingLeft: "20px" }}>
                        {errors.slice(0, 10).map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                        {errors.length > 10 && (
                          <li>... and {errors.length - 10} more errors</li>
                        )}
                      </ul>
                    </Alert>
                  </Col>
                </Row>
              )}

              {/* Batch preview */}
              {selectedData.length > 0 && (
                <Row className="px-4 mt-3">
                  <Col md={12}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "10px",
                      }}
                    >
                      <MDTypography variant="h6" style={{ color: "#2c3e50" }}>
                        Entries to submit ({selectedData.length})
                      </MDTypography>
                      <Button color="danger" size="sm" onClick={handleClearList}>
                        <i
                          className="fas fa-trash"
                          style={{ marginRight: "6px" }}
                        ></i>
                        Clear List
                      </Button>
                    </div>
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                      <Table bordered striped size="sm" style={{ fontSize: 12 }}>
                        <thead
                          style={{
                            position: "sticky",
                            top: 0,
                            backgroundColor: "#fff",
                            zIndex: 1,
                          }}
                        >
                          <tr>
                            <th>#</th>
                            <th>Admin Code</th>
                            <th>Admin Description</th>
                            <th>Economic Code</th>
                            <th>Economic Description</th>
                            <th>Program Code</th>
                            <th>Amount</th>
                            <th>Type</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedData.map((item, index) => (
                            <tr key={item.id ?? index}>
                              <td>{index + 1}</td>
                              <td>{item.admin_code}</td>
                              <td>{item.admin_description}</td>
                              <td>{item.economic_code}</td>
                              <td>{item.economic_description}</td>
                              <td>{item.program_code}</td>
                              <td style={{ textAlign: "right" }}>
                                {formatNumber(item.amount)}
                              </td>
                              <td>{item.input_type}</td>
                              <td style={{ textAlign: "center" }}>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleDeleteFromList(index)}
                                  style={{ padding: "2px 8px" }}
                                >
                                  <i className="fas fa-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ fontWeight: "bold" }}>
                            <td colSpan="6" style={{ textAlign: "right" }}>
                              Total Amount:
                            </td>
                            <td style={{ textAlign: "right", color: "#4caf50" }}>
                              {formatNumber(totalAmount)}
                            </td>
                            <td colSpan="2"></td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>

                    <center className="mb-3 mt-3">
                      <Button
                        color="success"
                        onClick={handleSubmit}
                        disabled={isLoading}
                        size="lg"
                      >
                        {isLoading ? (
                          <Spinner color="light" size="sm" />
                        ) : (
                          <>
                            <i
                              className="fas fa-paper-plane"
                              style={{ marginRight: "8px" }}
                            ></i>
                            Submit ({selectedData.length})
                          </>
                        )}
                      </Button>
                    </center>
                  </Col>
                </Row>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}
